/**
 * TBMuktBharat Super Admin Dashboard - 2026 Edition
 * Modern Government Portal with Advanced Analytics
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import api from '@/services/api'
import GovLayout from '@/components/layout/GovLayout'
import {
  Users, FileText, Video, Shield, Activity, TrendingUp, AlertTriangle,
  CheckCircle, Clock, Database, Server, Globe, Bell, Settings,
  BarChart3, PieChart, Zap, Eye, Download, RefreshCw, ChevronRight,
  UserCheck, UserX, FileBarChart, Calendar, MapPin, Search, Filter,
  Building2, Ban
} from 'lucide-react'

// ================================================================================
// ANIMATED COUNTER
// ================================================================================
const AnimatedCounter = ({ value, duration = 1000 }: { value: number; duration?: number }) => {
  const [count, setCount] = useState(0)

  useEffect(() => {
    let start = 0
    const end = value
    const incrementTime = duration / end
    const timer = setInterval(() => {
      start += Math.ceil(end / 50)
      if (start >= end) {
        setCount(end)
        clearInterval(timer)
      } else {
        setCount(start)
      }
    }, incrementTime)
    return () => clearInterval(timer)
  }, [value, duration])

  return <span>{count.toLocaleString()}</span>
}

// ================================================================================
// METRIC CARD - GLASSMORPHISM STYLE
// ================================================================================
const MetricCard = ({ icon: Icon, label, value, change, trend, color }: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: number
  change?: string
  trend?: 'up' | 'down' | 'neutral'
  color: 'blue' | 'green' | 'purple' | 'orange' | 'cyan' | 'pink'
}) => {
  const gradients = {
    blue: 'from-blue-500 to-cyan-400',
    green: 'from-green-500 to-emerald-400',
    purple: 'from-purple-500 to-pink-400',
    orange: 'from-orange-500 to-amber-400',
    cyan: 'from-cyan-500 to-blue-400',
    pink: 'from-pink-500 to-rose-400',
  }

  return (
    <div className="group relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-xl transition-all duration-300">
      <div className={`absolute inset-0 bg-gradient-to-br ${gradients[color]} opacity-0 group-hover:opacity-5 transition-opacity`} />
      <div className="p-5">
        <div className="flex items-start justify-between">
          <div className={`p-3 rounded-xl bg-gradient-to-br ${gradients[color]} text-white shadow-lg`}>
            <Icon className="w-6 h-6" />
          </div>
          {change && (
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
              trend === 'up' ? 'bg-green-100 text-green-700' :
              trend === 'down' ? 'bg-red-100 text-red-700' :
              'bg-gray-100 text-gray-700'
            }`}>
              {trend === 'up' && <TrendingUp className="w-3 h-3" />}
              {change}
            </div>
          )}
        </div>
        <div className="mt-4">
          <p className="text-sm font-medium text-gray-500">{label}</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">
            <AnimatedCounter value={value} />
          </p>
        </div>
      </div>
    </div>
  )
}

// ================================================================================
// ACTIVITY ITEM
// ================================================================================
const ActivityItem = ({ icon: Icon, title, description, time, type }: {
  icon: React.ComponentType<{ className?: string }>
  title: string
  description: string
  time: string
  type: 'success' | 'warning' | 'info' | 'error'
}) => {
  const typeStyles = {
    success: 'bg-green-100 text-green-600',
    warning: 'bg-amber-100 text-amber-600',
    info: 'bg-blue-100 text-blue-600',
    error: 'bg-red-100 text-red-600',
  }

  return (
    <div className="flex items-start gap-3 py-3 hover:bg-gray-50 -mx-2 px-2 rounded-lg transition-colors">
      <div className={`p-2 rounded-lg ${typeStyles[type]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-gray-900 text-sm">{title}</p>
        <p className="text-xs text-gray-500 truncate">{description}</p>
      </div>
      <span className="text-xs text-gray-400 whitespace-nowrap">{time}</span>
    </div>
  )
}

// ================================================================================
// QUICK ACTION BUTTON
// ================================================================================
const QuickAction = ({ icon: Icon, label, description, color, onClick }: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  description: string
  color: 'blue' | 'green' | 'purple' | 'orange' | 'cyan' | 'pink'
  onClick: () => void
}) => {
  const colors = {
    blue: 'hover:bg-blue-50 hover:border-blue-200 hover:text-blue-600',
    green: 'hover:bg-green-50 hover:border-green-200 hover:text-green-600',
    purple: 'hover:bg-purple-50 hover:border-purple-200 hover:text-purple-600',
    orange: 'hover:bg-orange-50 hover:border-orange-200 hover:text-orange-600',
    cyan: 'hover:bg-cyan-50 hover:border-cyan-200 hover:text-cyan-600',
    pink: 'hover:bg-pink-50 hover:border-pink-200 hover:text-pink-600',
  }

  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-gray-100 transition-all ${colors[color]} group`}
    >
      <div className="p-3 rounded-xl bg-gray-100 group-hover:bg-white shadow-sm transition-all">
        <Icon className="w-6 h-6" />
      </div>
      <div className="text-center">
        <p className="font-medium text-sm">{label}</p>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
    </button>
  )
}

// ================================================================================
// CHART PLACEHOLDER (Would use recharts in production)
// ================================================================================
const MiniChart = ({ data, color }: { data: number[]; color: string }) => (
  <div className="flex items-end gap-1 h-16">
    {data.map((value, i) => (
      <div
        key={i}
        className={`w-full rounded-t ${color}`}
        style={{ height: `${(value / Math.max(...data)) * 100}%` }}
      />
    ))}
  </div>
)

// ================================================================================
// SUPER ADMIN DASHBOARD
// ================================================================================
const SuperAdminDashboard = () => {
  const { user, isAuthenticated } = useAuthStore()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalPosts: 0,
    totalReels: 0,
    totalGroups: 0,
    totalPages: 0,
    pendingReports: 0,
    activeSessions: 0,
  })
  const [recentUsers, setRecentUsers] = useState<any[]>([])
  const [breakdown, setBreakdown] = useState<{
    byRole: Record<string, number>
    byType: Record<string, number>
    byStatus: { ACTIVE: number; INACTIVE: number; SUSPENDED: number; BANNED: number }
    newUsersToday: number
  }>({
    byRole: {},
    byType: {},
    byStatus: { ACTIVE: 0, INACTIVE: 0, SUSPENDED: 0, BANNED: 0 },
    newUsersToday: 0,
  })
  const [systemHealth, setSystemHealth] = useState({
    database: 'operational',
    api: 'operational',
    storage: 'operational',
    email: 'operational',
  })

  const fetchDashboardData = useCallback(async (showRefresh = false) => {
    try {
      if (showRefresh) setRefreshing(true)
      else setLoading(true)

      const [usersRes, postsRes, groupsRes, statsRes] = await Promise.all([
        api.get('/users?page=0&size=10').catch(() => ({ data: { success: false } })),
        api.get('/posts/feed?page=0&size=1').catch(() => ({ data: { success: false } })),
        api.get('/groups/public?page=0&size=1').catch(() => ({ data: { success: false } })),
        api.get('/admin/stats').catch(() => ({ data: { success: false } })),
      ])

      if (usersRes.data.success) {
        setStats(prev => ({
          ...prev,
          totalUsers: usersRes.data.data.totalElements || 0,
        }))
        setRecentUsers(usersRes.data.data.content || [])
      }

      if (postsRes.data.success) {
        setStats(prev => ({ ...prev, totalPosts: postsRes.data.data.totalElements || 0 }))
      }

      if (groupsRes.data.success) {
        setStats(prev => ({ ...prev, totalGroups: groupsRes.data.data.totalElements || 0 }))
      }

      // Real admin stats breakdown (role / type / status) + real active-user count
      if (statsRes.data.success && statsRes.data.data) {
        const s = statsRes.data.data
        setStats(prev => ({
          ...prev,
          totalUsers: typeof s.totalUsers === 'number' ? s.totalUsers : prev.totalUsers,
          activeUsers: s.activeUsers || 0,
          totalPosts: typeof s.totalPosts === 'number' ? s.totalPosts : prev.totalPosts,
        }))
        setBreakdown({
          byRole: s.roleDistribution || {},
          byType: s.userTypeDistribution || {},
          byStatus: {
            ACTIVE: s.activeUsers || 0,
            INACTIVE: s.inactiveUsers || 0,
            SUSPENDED: s.suspendedUsers || 0,
            BANNED: s.bannedUsers || 0,
          },
          newUsersToday: s.newUsersToday || 0,
        })
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }

    if (user && user.role !== 'SUPER_ADMIN') {
      navigate('/feed')
      return
    }

    fetchDashboardData()
  }, [user, isAuthenticated, navigate, fetchDashboardData])

  // Sample chart data
  const chartData = useMemo(() => ({
    users: [120, 150, 180, 200, 250, 280, 320],
    posts: [45, 60, 75, 90, 85, 110, 130],
    engagement: [65, 72, 80, 85, 90, 88, 95],
  }), [])

  // Recent activities
  const recentActivities = useMemo(() => [
    { icon: UserCheck, title: 'New User Registered', description: 'user@tbmuktbharat.org', time: '2 min ago', type: 'success' as const },
    { icon: FileText, title: 'Post Approved', description: 'Post #12345 approved by admin', time: '5 min ago', type: 'success' as const },
    { icon: AlertTriangle, title: 'Report Pending', description: '3 reports awaiting review', time: '10 min ago', type: 'warning' as const },
    { icon: Shield, title: 'Security Alert', description: 'Unusual login activity detected', time: '1 hour ago', type: 'error' as const },
    { icon: Database, title: 'Backup Complete', description: 'Daily backup completed successfully', time: '2 hours ago', type: 'success' as const },
  ], [])

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white font-medium">Loading Dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <GovLayout>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Banner */}
        <div className="mb-8 p-6 rounded-2xl bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-600 text-white shadow-xl">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold">Welcome back, {user?.firstName}! 👋</h2>
              <p className="text-white/80 mt-1">Here's what's happening across the platform today.</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="px-4 py-2 bg-white/20 rounded-lg backdrop-blur-sm">
                <p className="text-sm text-white/80">Last Login</p>
                <p className="font-medium">{new Date().toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' })}</p>
              </div>
              <div className="px-4 py-2 bg-white/20 rounded-lg backdrop-blur-sm">
                <p className="text-sm text-white/80">Role</p>
                <p className="font-medium">Super Admin</p>
              </div>
            </div>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <MetricCard icon={Users} label="Total Users" value={stats.totalUsers} change="+12%" trend="up" color="blue" />
          <MetricCard icon={FileText} label="Total Posts" value={stats.totalPosts} change="+8%" trend="up" color="green" />
          <MetricCard icon={Video} label="Total Reels" value={stats.totalReels} change="+25%" trend="up" color="purple" />
          <MetricCard icon={Activity} label="Active Sessions" value={stats.activeUsers} change="+5%" trend="up" color="cyan" />
        </div>

        {/* User Breakdown - by role, type, status */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* By Role */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-5 h-5 text-blue-600" />
              <h3 className="font-bold text-gray-900">Users by Role</h3>
            </div>
            <div className="space-y-3">
              {[
                { key: 'USER', label: 'User', color: 'bg-gray-100 text-gray-700' },
                { key: 'ADMIN', label: 'Admin', color: 'bg-blue-100 text-blue-700' },
                { key: 'SUPER_ADMIN', label: 'Super Admin', color: 'bg-red-100 text-red-700' },
              ].map(r => {
                const count = breakdown.byRole[r.key] || 0
                const pct = stats.totalUsers ? Math.round((count / stats.totalUsers) * 100) : 0
                return (
                  <div key={r.key}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.color}`}>{r.label}</span>
                      <span className="text-sm font-bold text-gray-900">{count.toLocaleString()} <span className="text-gray-400 font-normal">({pct}%)</span></span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* By User Type */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-emerald-600" />
              <h3 className="font-bold text-gray-900">Users by Type</h3>
            </div>
            <div className="space-y-3">
              {[
                { key: 'USER', label: 'General User', icon: Users, color: 'bg-gray-400' },
                { key: 'NGO', label: 'NGO', icon: Building2, color: 'bg-indigo-500' },
              ].map(t => {
                const count = breakdown.byType[t.key] || 0
                const pct = stats.totalUsers ? Math.round((count / stats.totalUsers) * 100) : 0
                return (
                  <div key={t.key}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="inline-flex items-center gap-2 text-sm text-gray-700">
                        <t.icon className="w-4 h-4 text-gray-400" />
                        {t.label}
                      </span>
                      <span className="text-sm font-bold text-gray-900">{count.toLocaleString()} <span className="text-gray-400 font-normal">({pct}%)</span></span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${t.color}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* By Status */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <Activity className="w-5 h-5 text-cyan-600" />
              <h3 className="font-bold text-gray-900">Users by Status</h3>
            </div>
            <div className="space-y-3">
              {[
                { key: 'ACTIVE', label: 'Active', icon: UserCheck, color: 'text-green-600', bar: 'bg-green-500' },
                { key: 'INACTIVE', label: 'Inactive', icon: Clock, color: 'text-yellow-600', bar: 'bg-yellow-500' },
                { key: 'SUSPENDED', label: 'Suspended', icon: UserX, color: 'text-orange-600', bar: 'bg-orange-500' },
                { key: 'BANNED', label: 'Banned', icon: Ban, color: 'text-red-600', bar: 'bg-red-500' },
              ].map(s => {
                const count = breakdown.byStatus[s.key as keyof typeof breakdown.byStatus] || 0
                const pct = stats.totalUsers ? Math.round((count / stats.totalUsers) * 100) : 0
                return (
                  <div key={s.key}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`inline-flex items-center gap-2 text-sm ${s.color}`}>
                        <s.icon className="w-4 h-4" />
                        {s.label}
                      </span>
                      <span className="text-sm font-bold text-gray-900">{count.toLocaleString()} <span className="text-gray-400 font-normal">({pct}%)</span></span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${s.bar}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                )
              })}
              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <span className="inline-flex items-center gap-2 text-sm text-blue-600">
                  <TrendingUp className="w-4 h-4" />
                  New Today
                </span>
                <span className="text-sm font-bold text-gray-900">{breakdown.newUsersToday.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Charts & Activity */}
          <div className="lg:col-span-2 space-y-6">
            {/* Charts Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-gray-900">User Growth</h3>
                  <span className="text-xs text-green-600 font-medium">+18% this week</span>
                </div>
                <MiniChart data={chartData.users} color="bg-blue-400" />
              </div>
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-gray-900">Post Activity</h3>
                  <span className="text-xs text-purple-600 font-medium">+24% this week</span>
                </div>
                <MiniChart data={chartData.posts} color="bg-purple-400" />
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="font-bold text-gray-900 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <QuickAction icon={Users} label="Users" description="Manage accounts" color="blue" onClick={() => navigate('/admin/users')} />
                <QuickAction icon={FileText} label="Content" description="Moderate posts" color="green" onClick={() => navigate('/admin/content')} />
                <QuickAction icon={Shield} label="Reports" description="Review flags" color="orange" onClick={() => navigate('/admin/reports')} />
                <QuickAction icon={Settings} label="Settings" description="Configuration" color="purple" onClick={() => navigate('/admin/settings')} />
                <QuickAction icon={Globe} label="Pages" description="Manage pages" color="cyan" onClick={() => navigate('/pages')} />
                <QuickAction icon={Users} label="Groups" description="Manage groups" color="pink" onClick={() => navigate('/groups')} />
                <QuickAction icon={Bell} label="Notifications" description="Send alerts" color="blue" onClick={() => {}} />
                <QuickAction icon={Download} label="Export Data" description="Download reports" color="green" onClick={() => {}} />
                <QuickAction icon={FileBarChart} label="Oath Analytics" description="Who took the pledge" color="cyan" onClick={() => navigate('/admin/oaths')} />
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900">Recent Activity</h3>
                <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">View All</button>
              </div>
              <div className="divide-y divide-gray-100">
                {recentActivities.map((activity, i) => (
                  <ActivityItem key={i} {...activity} />
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* System Health */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="font-bold text-gray-900 mb-4">System Health</h3>
              <div className="space-y-3">
                {[
                  { label: 'Database', status: systemHealth.database, icon: Database },
                  { label: 'API Server', status: systemHealth.api, icon: Server },
                  { label: 'Storage', status: systemHealth.storage, icon: FileBarChart },
                  { label: 'Email Service', status: systemHealth.email, icon: Globe },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-2">
                      <item.icon className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-600">{item.label}</span>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      item.status === 'operational' ? 'bg-green-100 text-green-700' :
                      item.status === 'degraded' ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {item.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Users */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900">Recent Users</h3>
                <button
                  onClick={() => navigate('/admin/users')}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                >
                  View All <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-3">
                {recentUsers.slice(0, 5).map((u: any) => (
                  <div key={u.id} className="flex items-center gap-3 py-2">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-sm font-medium">
                      {u.firstName?.[0]}{u.lastName?.[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 text-sm truncate">{u.firstName} {u.lastName}</p>
                      <p className="text-xs text-gray-500 truncate">{u.email}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      u.role === 'SUPER_ADMIN' ? 'bg-red-100 text-red-700' :
                      u.role === 'ADMIN' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {u.role}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Platform Stats */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl shadow-lg p-6 text-white">
              <h3 className="font-bold mb-4">Platform Overview</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Total Groups</span>
                  <span className="font-bold text-xl">{stats.totalGroups.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Total Pages</span>
                  <span className="font-bold text-xl">{stats.totalPages.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Pending Reports</span>
                  <span className="font-bold text-xl text-amber-400">{stats.pendingReports}</span>
                </div>
                <div className="pt-4 border-t border-slate-700">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-cyan-400" />
                    <span className="text-sm text-slate-400">System Load</span>
                    <span className="ml-auto font-medium text-green-400">Normal</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </GovLayout>
  )
}

export default React.memo(SuperAdminDashboard)